import { Injectable } from '@angular/core';
import {
  Activity,
  CreateActivityPayload,
  LocationSuggestion,
  MetricReading,
  ReachSummary,
  SourceDefinition,
  currentReach,
} from '../core/activity.model';
import { UserProfile } from '../core/profile-api.service';
import {
  DemoInsight,
  seedDemoActivities,
  seedDemoInsights,
} from './demo-data';

/**
 * The demo's "database": everything the DemoApiInterceptor serves lives in
 * this in-memory store. Seeded lazily on first access; a page reload starts
 * fresh (which is exactly what the demo banner's Reset button relies on).
 */
@Injectable({ providedIn: 'root' })
export class DemoStoreService {
  private _activities: Activity[] | null = null;
  private _nextId = 1;

  private _profile: UserProfile = {
    first_name: 'Sani',
    last_name: 'Yusuf',
    title: 'Developer Advocate',
    company: 'Advocad',
    bio: 'Talks, videos, posts and podcasts on Angular, the web and AI — all tracked here. (Interactive demo: nothing is saved.)',
    location: 'London, United Kingdom',
    social_links: [],
  };

  get activities(): Activity[] {
    if (!this._activities) this._activities = seedDemoActivities();
    return this._activities;
  }

  find(id: string): Activity | undefined {
    return this.activities.find((a) => a._id === id);
  }

  create(payload: CreateActivityPayload): Activity {
    const now = new Date().toISOString();
    const activity: Activity = {
      _id: `local-demo-added-${this._nextId++}`,
      title: payload.title,
      type: payload.type,
      date: payload.date,
      description: payload.description,
      url: payload.url,
      slides_url: payload.slides_url,
      thumbnail: payload.thumbnail,
      source: payload.source,
      is_online: payload.is_online,
      location: payload.location,
      tags: payload.tags,
      metrics_list: [
        {
          captured_date: now,
          metrics: payload.metrics ?? [],
          fetched_via: payload.fetched_via ?? 'manual',
          fetch_duration_ms: payload.fetch_duration_ms,
        },
      ],
      created_date: now,
      updated_date: now,
    };
    this.activities.unshift(activity);
    return activity;
  }

  /** PATCH semantics mirror the API: `metrics` appends a new reading. */
  update(id: string, patch: Partial<CreateActivityPayload>): Activity | undefined {
    const activity = this.find(id);
    if (!activity) return undefined;
    const { metrics, ...meta } = patch;
    Object.assign(activity, meta);
    if (metrics) {
      activity.metrics_list = [
        ...activity.metrics_list,
        { captured_date: new Date().toISOString(), metrics },
      ];
    }
    activity.updated_date = new Date().toISOString();
    return activity;
  }

  remove(id: string): boolean {
    const before = this.activities.length;
    this._activities = this.activities.filter((a) => a._id !== id);
    return this._activities.length < before;
  }

  readings(id: string): MetricReading[] {
    return this.find(id)?.metrics_list ?? [];
  }

  /**
   * A small fixed insight feed so the demo replica of the signed-in app looks
   * alive. Cards reference real demo activities so the "view the post" link
   * works. Nothing is persisted, and acting on a card is a client-side no-op.
   */
  private _insights: DemoInsight[] | null = null;

  insights(): DemoInsight[] {
    if (!this._insights) this._insights = seedDemoInsights();
    return this._insights;
  }

  insight(id: string): DemoInsight | undefined {
    return this.insights().find((i) => i._id === id);
  }

  insightsForActivity(activityId: string): DemoInsight[] {
    return this.insights().filter((i) => i.refs.includes(activityId));
  }

  /** Simulated re-crawl: nudge the auto metrics up ~4% as a new reading. */
  refresh(id: string): Activity | undefined {
    const activity = this.find(id);
    if (!activity) return undefined;
    const last = activity.metrics_list[activity.metrics_list.length - 1];
    const bumped = (last?.metrics ?? []).map((m) =>
      m.is_manual ? { ...m } : { ...m, reach: Math.round(m.reach * 1.04) },
    );
    activity.metrics_list = [
      ...activity.metrics_list,
      {
        captured_date: new Date().toISOString(),
        metrics: bumped,
        fetched_via: 'yt_dlp',
        fetch_duration_ms: 640,
      },
    ];
    return activity;
  }

  summary(): ReachSummary {
    let total_reach = 0;
    const byType = new Map<string, { reach: number; count: number }>();
    const bySource = new Map<string, { reach: number; count: number }>();
    for (const a of this.activities) {
      const reach = currentReach(a);
      total_reach += reach;
      const t = byType.get(a.type) ?? { reach: 0, count: 0 };
      t.reach += reach;
      t.count += 1;
      byType.set(a.type, t);
      const key = a.source ?? 'other';
      const s = bySource.get(key) ?? { reach: 0, count: 0 };
      s.reach += reach;
      s.count += 1;
      bySource.set(key, s);
    }
    return {
      total_reach,
      count: this.activities.length,
      by_type: [...byType.entries()].map(([type, v]) => ({
        type: type as ReachSummary['by_type'][number]['type'],
        ...v,
      })),
      by_source: [...bySource.entries()].map(([source, v]) => ({
        source,
        ...v,
      })),
    };
  }

  get profile(): UserProfile {
    return this._profile;
  }

  updateProfile(patch: Partial<UserProfile>): UserProfile {
    this._profile = { ...this._profile, ...patch };
    return this._profile;
  }

  readonly sources: SourceDefinition[] = [
    { source: 'youtube', label: 'YouTube', metrics: ['views', 'likes', 'comments'] },
    { source: 'x', label: 'X', metrics: ['impressions', 'likes', 'reposts'] },
    { source: 'linkedin', label: 'LinkedIn', metrics: ['impressions', 'reactions', 'comments'] },
    { source: 'tiktok', label: 'TikTok', metrics: ['views', 'likes'] },
    { source: 'instagram', label: 'Instagram', metrics: ['views', 'likes'] },
    { source: 'twitch', label: 'Twitch', metrics: ['views', 'peak live'] },
    { source: 'spotify', label: 'Spotify', metrics: ['plays'] },
    { source: 'medium', label: 'Medium', metrics: ['views', 'reads'] },
    { source: 'devto', label: 'DEV', metrics: ['views', 'reactions'] },
    { source: 'meetup', label: 'Meetup', metrics: ['attendees'] },
    { source: 'web', label: 'Web', metrics: ['views'] },
  ];

  private readonly _locations: LocationSuggestion[] = [
    { place_name: 'London, United Kingdom', city: 'London', country: 'United Kingdom', country_code: 'GB', latitude: 51.5074, longitude: -0.1278 },
    { place_name: 'Lagos, Nigeria', city: 'Lagos', country: 'Nigeria', country_code: 'NG', latitude: 6.5244, longitude: 3.3792 },
    { place_name: 'San Francisco, United States', city: 'San Francisco', country: 'United States', country_code: 'US', latitude: 37.7749, longitude: -122.4194 },
    { place_name: 'Berlin, Germany', city: 'Berlin', country: 'Germany', country_code: 'DE', latitude: 52.52, longitude: 13.405 },
    { place_name: 'Amsterdam, Netherlands', city: 'Amsterdam', country: 'Netherlands', country_code: 'NL', latitude: 52.3676, longitude: 4.9041 },
    { place_name: 'New York, United States', city: 'New York', country: 'United States', country_code: 'US', latitude: 40.7128, longitude: -74.006 },
  ];

  geocode(q: string): LocationSuggestion[] {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return this._locations.filter((l) =>
      l.place_name.toLowerCase().includes(needle),
    );
  }

  /** Topic tags from the title — always technical, always tame. */
  suggestTags(title: string, type?: string): string[] {
    const t = (title || '').toLowerCase();
    const tags = new Set<string>();
    if (t.includes('angular')) tags.add('angular');
    if (t.includes('react')) tags.add('react');
    if (/\bai\b|llm|agent|copilot|claude|gpt/.test(t)) tags.add('ai');
    if (t.includes('typescript')) tags.add('typescript');
    if (type === 'speaking' || type === 'workshop') tags.add('community');
    tags.add('web');
    if (tags.size < 3) tags.add('devrel');
    return [...tags].slice(0, 5);
  }
}
